import React, { useCallback, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ArrowDownward from '@mui/icons-material/ArrowDownward';

export type DiscoverySortDir = 'asc' | 'desc';

export interface DiscoverySortColumn<K extends string> {
  key: K;
  label: string;
  sx: object;
}

function compareDiscoveryText(a: string, b: string, dir: DiscoverySortDir): number {
  const cmp = String(a || '').localeCompare(String(b || ''), undefined, {
    sensitivity: 'base',
    numeric: true,
  });
  return dir === 'asc' ? cmp : -cmp;
}

export function useDiscoveryColumnSort<T, K extends string>(
  items: T[],
  getValue: (item: T, key: K) => string,
) {
  const [sort, setSort] = useState<{ key: K; dir: DiscoverySortDir } | null>(null);

  const toggleSort = useCallback((key: K) => {
    setSort((prev) => {
      if (prev?.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { key, dir: 'asc' };
    });
  }, []);

  const sorted = useMemo(() => {
    if (!sort) return items;
    return [...items].sort((a, b) => (
      compareDiscoveryText(getValue(a, sort.key), getValue(b, sort.key), sort.dir)
    ));
  }, [items, getValue, sort]);

  return {
    sorted,
    sortKey: sort?.key ?? null,
    sortDir: sort?.dir ?? 'asc' as DiscoverySortDir,
    toggleSort,
  };
}

interface HeaderProps<K extends string> {
  columns: DiscoverySortColumn<K>[];
  sortKey: K | null;
  sortDir: DiscoverySortDir;
  onToggle: (key: K) => void;
}

export function DiscoverySortableHeader<K extends string>({
  columns,
  sortKey,
  sortDir,
  onToggle,
}: HeaderProps<K>) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 0.35,
        borderBottom: '1px solid #333',
        mb: 0.25,
        minWidth: 0,
      }}
    >
      {columns.map((col) => {
        const active = col.key === sortKey;
        return (
          <Box
            key={col.key}
            component="button"
            type="button"
            onClick={() => onToggle(col.key)}
            aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
            sx={{
              ...col.sx,
              appearance: 'none',
              background: 'transparent',
              border: 0,
              p: 0,
              m: 0,
              minHeight: 28,
              overflow: 'hidden',
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: '0.03em',
              color: active ? '#2391FF' : '#888',
              cursor: 'pointer',
              userSelect: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 0.15,
              textAlign: 'left',
              WebkitTapHighlightColor: 'transparent',
              '&:hover': { color: active ? '#4aa6ff' : '#bbb' },
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: 'inherit',
                fontWeight: 'inherit',
                color: 'inherit',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.2,
                minWidth: 0,
                flex: 1,
              }}
            >
              {col.label}
            </Typography>
            {active && (
              sortDir === 'asc'
                ? <ArrowUpward sx={{ fontSize: 12, flexShrink: 0 }} />
                : <ArrowDownward sx={{ fontSize: 12, flexShrink: 0 }} />
            )}
          </Box>
        );
      })}
    </Box>
  );
}
